// dashboard/index.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import axios, { setAuthToken } from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { userLogin } from "store/reducers/auth";
import dayjs from "dayjs";
import GroupsIcon from "@mui/icons-material/Groups";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import GrainIcon from "@mui/icons-material/Grain";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Autocomplete,
  Button,
  FormControl,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Plus, Package, UserPlus, Layers, FilePlus } from "lucide-react";
import StockManagerDashboard from "./stockmanagerDashboard";
import SoftTypography from "components/SoftTypography";
import { RootState } from "store";
import SlideOverPanel from "components/SlideOverPanel";
import AddCustomers from "layouts/customers/AddCustomers";
import AddEmployee from "layouts/employees/AddEmployee";
import AddSubscription from "layouts/Subscriptions/AddSubscription";
import AddPage from "layouts/Pages/AddPage";
import AdminAddTicket from "layouts/AdminTickets/AdminAddTicket";
import AddAsset from "layouts/Assets/AddAsset";
import AddAssetType from "layouts/AssetTypes/AddAssetType";
import BranchHeadDashboard from "./BranchHeadDashboard";
import { formatMoney } from "utils/money";

interface Ticket {
  title: string;
  customer_name: string;
  employee_name: string;
  ticket_id: number;
  ticket_service_id: string;
  description: string;
  created_at: Date;
  status_name: string;
  employee_arrival_date: Date;
  employee_arrival_time?: string | null;
  assigned_on?: string | null;
  priority_rank: number | string;
  company_name: string;
}

/** Parse scheduled arrival; time is required for correct clock placement in calendars. */
function parseTicketArrivalDateTime(
  dateVal: string | Date | undefined,
  timeVal?: string | null
): Date | null {
  if (!dateVal) return null;
  const extractDateParts = (
    value: string | Date
  ): { year: number; month: number; day: number } | null => {
    if (typeof value === "string") {
      const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return null;
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  };
  const parts = extractDateParts(dateVal);
  if (!parts) return null;
  if (timeVal != null && String(timeVal).trim() !== "") {
    const t = String(timeVal).trim();
    const tm = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (tm) {
      const hour = Number(tm[1]);
      const minute = Number(tm[2]);
      const second = tm[3] ? Number(tm[3]) : 0;
      if (hour <= 23 && minute <= 59 && second <= 59) {
        return new Date(
          parts.year,
          parts.month - 1,
          parts.day,
          hour,
          minute,
          second
        );
      }
    }
  }
  return null;
}

function parseArrivalDateOnlyLocal(
  dateVal: string | Date | undefined
): Date | null {
  if (!dateVal) return null;
  if (typeof dateVal === "string") {
    const match = dateVal.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }
  return new Date(
    dateVal.getUTCFullYear(),
    dateVal.getUTCMonth(),
    dateVal.getUTCDate()
  );
}

function ticketOnCalendarDay(
  ticket: Pick<Ticket, "employee_arrival_date" | "employee_arrival_time">,
  day: Date
): boolean {
  const withTime = parseTicketArrivalDateTime(
    ticket.employee_arrival_date,
    ticket.employee_arrival_time
  );
  if (withTime) return withTime.toDateString() === day.toDateString();
  const d = parseArrivalDateOnlyLocal(ticket.employee_arrival_date);
  return d != null && d.toDateString() === day.toDateString();
}

type SafePieLabelProps = {
  cx?: number | string;
  cy?: number | string;
  midAngle?: number;
  outerRadius?: number | string;
  percent?: number;
  name?: string;
};

const statusColors: Record<string, string> = {
  Open: "#f44336",
  ToDo: "#ff9800",
  "In-Progress": "#1976d2",
  "In Progress": "#1976d2",
  "On-Hold": "#0288d1",
  Pending: "#9c27b0",
  Done: "#4caf50",
  Closed: "#9e9e9e",
  ReOpen: "#ffa726",
  Cancelled: "#d32f2f",
};

interface TicketCalendar {
  employee_arrival_date: Date;
  employee_arrival_time?: string | null;
}

interface TicketCalendarProps {
  tickets: TicketCalendar[];
  onDateSelect: (date: Date) => void;
}

interface CountItem {
  total_count?: number;
  status_id: number;
}

// Helper function to get status indicator color
const getStatusIndicatorColor = (daysLeft: number): string => {
  if (daysLeft <= 3) {
    return "error.main";
  }
  if (daysLeft <= 7) {
    return "warning.main";
  }
  return "success.main";
};

// --- Extracted: slide panel title helper ---
const getSlideOverTitle = (panel: string | null): string => {
  const titles: Record<string, string> = {
    "add-member": "Add Member",
    "add-customer": "Add Customer",
    "add-subscription": "Add Subscription",
    "add-page": "Add Page",
    "add-ticket": "Add Ticket",
    "add-asset": "Add Asset",
    "add-asset-type": "Add Asset Type",
  };
  return panel ? (titles[panel] ?? "") : "";
};

// --- Extracted: check if user is admin (handles both array and string Role) ---
const isUserAdmin = (role: string | string[] | undefined): boolean => {
  if (Array.isArray(role)) {
    return role.some((r: string) => String(r).toLowerCase() === "admin");
  }
  return String(role ?? "").toLowerCase() === "admin";
};

// --- Extracted: compute safe rating value ---
const computeRatingFromRaw = (raw: unknown): number => {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }
  if (Array.isArray(raw)) {
    if (raw.length === 0) return 0;
    return raw.reduce((s: number, r: { rating?: unknown }) => s + (Number(r?.rating) || 0), 0) / raw.length;
  }
  const parsed = parseFloat(raw as string);
  return parsed;
};

// --- Extracted: filter non-admin employees ---
const isNonAdminEmployee = (emp: Record<string, unknown>): boolean => {
  const roleSource =
    (typeof emp.role_names === "string" && emp.role_names) ||
    (typeof emp.role_name === "string" && emp.role_name) ||
    "";
  const normalizedRoles = roleSource
    .split(",")
    .map((role: string) => role.trim().toLowerCase());
  return !normalizedRoles.some(
    (role: string) => role === "admin" || role === "super admin"
  );
};

const TicketCalendar = ({ tickets, onDateSelect }: TicketCalendarProps) => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today);

  const getDaysInMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getFirstDayOfMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const handleDateClick = (day: number | undefined) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(newDate);
    onDateSelect(newDate);
  };

  const goToPreviousMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );

  const goToNextMonth = () =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);

  useEffect(() => {
    onDateSelect(today);
  }, []);

  return (
    <Card sx={{ p: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Chip
            label="<"
            onClick={goToPreviousMonth}
            sx={{ cursor: "pointer", minHeight: 24, "& .MuiChip-label": { px: 0.5, fontSize: "0.75rem" } }}
          />
          <Chip
            label=">"
            onClick={goToNextMonth}
            sx={{ cursor: "pointer", minHeight: 24, "& .MuiChip-label": { px: 0.5, fontSize: "0.75rem" } }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          textAlign: "center",
        }}
      >
        {dayNames.map((day, i) => (
          <Typography
            key={`${day}-${i}`}
            variant="caption"
            sx={{
              fontWeight: "bold",
              fontSize: "0.65rem",
              display: "block",
              textAlign: "center",
            }}
          >
            {day}
          </Typography>
        ))}

        {Array.from({ length: firstDay }).map((_, index) => (
          <Box key={`empty-${firstDay}-${index}`} sx={{ height: 28 }} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const date = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day
          );
          const dateString = date.toDateString();
          const hasTickets = tickets.some((t) =>
            ticketOnCalendarDay(
              t as Pick<Ticket, "employee_arrival_date" | "employee_arrival_time">,
              date
            )
          );
          const isSelected = selectedDate.toDateString() === dateString;
          const isToday = today.toDateString() === dateString;

          let background: string;
          if (isSelected) {
            background = "linear-gradient(135deg, #FAD7A1, #f5c4b0)";
          } else if (isToday) {
            background = "#e0e7ff";
          } else {
            background = "transparent";
          }

          return (
            <Box
              key={day}
              sx={{
                height: 28,
                cursor: "pointer",
                background,
                color: isSelected ? "black" : "inherit",
                borderRadius: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                "&:hover": { background: "#f3f4f6" },
              }}
              onClick={() => handleDateClick(day)}
            >
              <Typography sx={{ fontSize: "0.7rem" }}>{day}</Typography>
              {hasTickets && (
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #FAD7A1, #f5c4b0)",
                    position: "absolute",
                    bottom: 2,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

TicketCalendar.propTypes = {
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      employee_arrival_date: PropTypes.string.isRequired,
    })
  ).isRequired,
  onDateSelect: PropTypes.func.isRequired,
};

interface Subscription {
  id: number;
  company_name: string;
  end_date: string;
  name: string;
  asset_name: string;
  price: number;
}

interface ExpiringSubscription extends Subscription {
  daysLeft: number;
}

// ─── Extracted data-fetch hooks ────────────────────────────────────────────────

const useDashboardData = (user: any, refreshInterval: number) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [counts, setCounts] = useState<Record<string, CountItem>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [rating, setRating] = useState<number | null>(null);
  const [todayTickets, setTodayTickets] = useState([]);
  const [latestTickets, setLatestTickets] = useState([]);
  const [activeCustomerCount, setActiveCustomerCount] = useState(0);
  const [inActiveCustomerCount, setInActiveCustomerCount] = useState(0);
  const [activeEmployeeCount, setActiveEmployeeCount] = useState(0);
  const [inActiveEmployeeCount, setInActiveEmployeeCount] = useState(0);
  const [activeAssetTypeCount, setActiveAssetTypeCount] = useState(0);
  const [inActiveAssetTypeCount, setInActiveAssetTypeCount] = useState(0);
  const [assetCount, setAssetCount] = useState(0);
  const [inActiveAssetCount, setInActiveAssetCount] = useState(0);
  const [revenue, setRevenue] = useState(0);

  useEffect(() => {
    axios
      .get("/auth")
      .then((res) => dispatch(userLogin({ user: res.data })))
      .catch((err) => {
        if (err?.response?.status === 402) {
          setAuthToken(null);
          const msg =
            err?.response?.data?.error ||
            "No active subscription. Please contact super admin.";
          try {
            sessionStorage.setItem("authError", msg);
          } catch (_) {}
          dispatch(userLogin({ user: null }));
          navigate("/home");
        }
      });
  }, [dispatch, navigate]);

  const fetchTicketCounts = useCallback(async () => {
    try {
      let response;
      if (user?.Role.includes("employee")) {
        response = await axios.get(`/tickets/employee/ticket-counts/${user?.userId}`);
      } else {
        response = await axios.get("/tickets/ticket-counts");
      }
      const rawEntries = Object.entries(response?.data?.list ?? {}) as [string, CountItem][];
      const hideOpenForEmployee = user?.Role.includes("employee");
      const filteredEntries = hideOpenForEmployee
        ? rawEntries.filter(([status]) => status?.trim().toLowerCase() !== "open")
        : rawEntries;
      const filteredCounts = Object.fromEntries(filteredEntries) as Record<string, CountItem>;
      const newTotalCount = filteredEntries.reduce(
        (acc, [, curr]) => acc + (curr?.total_count ?? 0),
        0
      );
      setCounts(filteredCounts);
      setTotalCount(newTotalCount);
    } catch (error) {
      console.error(error);
    }
  }, [user]);

  const fetchEmployeeTickets = useCallback(async () => {
    try {
      if (user?.Role.includes("employee")) {
        const response = await axios.get(`/tickets/employee/${user?.userId}`);
        setTodayTickets(response.data.list);
      }
    } catch (error) {
      console.error("Failed to fetch employee tickets", error);
    }
  }, [user]);

  const fetchEmployeeRating = useCallback(async () => {
    try {
      if (user?.Role.includes("employee")) {
        const response = await axios.get(`/rating/employee/${user.userId}`);
        const raw = response.data?.list;
        const num = computeRatingFromRaw(raw);
        setRating(Number.isFinite(num) ? num : 0);
      }
    } catch (error) {
      console.error("Failed to fetch rating", error);
    }
  }, [user]);

  const fetchLatestTickets = useCallback(async () => {
    try {
      const response = await axios.get("/tickets/latest");
      setLatestTickets(response.data.list ?? []);
    } catch (err) {
      console.error("Error fetching latest tickets:", err);
    }
  }, []);

  const fetchActiveCustomerCount = useCallback(async () => {
    try {
      const res = await axios.get("/customer");
      const customers = res.data.list ?? [];
      setActiveCustomerCount(customers.filter((c: { is_active: number }) => c.is_active === 1).length);
      setInActiveCustomerCount(customers.filter((c: { is_active: number }) => c.is_active === 0).length);
    } catch (error) {
      console.error("Error fetching active customers:", error);
    }
  }, []);

  const fetchActiveEmployees = useCallback(async () => {
    try {
      const res = await axios.get("/employee");
      const employees = res.data.list ?? [];
      const nonAdminEmployees = employees.filter(isNonAdminEmployee);
      setActiveEmployeeCount(nonAdminEmployees.filter((e: { is_active: number }) => e.is_active === 1).length);
      setInActiveEmployeeCount(nonAdminEmployees.filter((e: { is_active: number }) => e.is_active === 0).length);
    } catch (error) {
      console.error("Error fetching active employees:", error);
    }
  }, []);

  const fetchAssetCount = useCallback(async () => {
    try {
      const res = await axios.get("/assets");
      const assets = res.data.list;
      setAssetCount(assets.filter((a: { is_active: number }) => a.is_active === 1).length);
      setInActiveAssetCount(assets.filter((a: { is_active: number }) => a.is_active === 0).length);
    } catch (error) {
      console.error("Error fetching asset count:", error);
    }
  }, []);

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await axios.get("subscription/revenue");
      setRevenue(res.data?.revenue);
    } catch (error) {
      console.error("Error fetching Revenue", error);
    }
  }, []);

  const fetchActiveAssetTypeCount = useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const assets = res.data.list;
      setActiveAssetTypeCount(assets.filter((a: { is_active: number }) => a.is_active === 1).length);
      setInActiveAssetTypeCount(assets.filter((a: { is_active: number }) => a.is_active === 0).length);
    } catch (error) {
      console.error("Error fetching active asset types:", error);
    }
  }, []);

  useEffect(() => {
    fetchActiveAssetTypeCount();
    fetchAssetCount();
    fetchActiveEmployees();
    fetchActiveCustomerCount();
    fetchLatestTickets();
    fetchTicketCounts();
    fetchEmployeeRating();
    fetchEmployeeTickets();
    fetchRevenue();

    if (refreshInterval > 0) {
      const interval = setInterval(fetchTicketCounts, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [user, refreshInterval]);

  return {
    counts,
    totalCount,
    rating,
    todayTickets,
    latestTickets,
    activeCustomerCount,
    inActiveCustomerCount,
    activeEmployeeCount,
    inActiveEmployeeCount,
    activeAssetTypeCount,
    inActiveAssetTypeCount,
    assetCount,
    inActiveAssetCount,
    revenue,
  };
};

// ─── Extracted: subscription data hook ─────────────────────────────────────────

const useSubscriptionData = () => {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const [dataMonths, setDataMonths] = useState<{ month: string; new: number; active: number }[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [expiringSoon, setExpiringSoon] = useState<ExpiringSubscription[]>([]);

  const countNewSubscriptions = (list: { start_date: string | number | Date }[], monthIndex: number) =>
    list.filter((s) => new Date(s.start_date).getMonth() === monthIndex).length;

  const countActiveSubscriptions = (list: { start_date: string | number | Date; end_date: string | number | Date }[], monthIndex: number) => {
    const year = new Date().getFullYear();
    const startOfMonth = new Date(year, monthIndex, 1);
    const endOfMonth = new Date(year, monthIndex + 1, 0);
    return list.filter((s) => {
      const start = new Date(s.start_date);
      const end = new Date(s.end_date);
      return start <= endOfMonth && end >= startOfMonth;
    }).length;
  };

  const fetchSubscriptionData = async () => {
    try {
      const res = await axios.get("/subscription");
      const list = res.data.list ?? res.data ?? [];
      const result = months.map((m, index) => ({
        month: m,
        new: countNewSubscriptions(list, index),
        active: countActiveSubscriptions(list, index),
      }));
      setDataMonths(result);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get("/subscription");
      setSubscriptions(res.data);
    } catch (error) {
      console.error("Error fetching subscriptions", error);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (!subscriptions) return;
    const today = new Date();
    const filtered = subscriptions
      .map((sub) => {
        const endDate = new Date(sub.end_date);
        const diffTime = endDate.getTime() - today.getTime();
        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return { ...sub, daysLeft };
      })
      .filter((sub) => sub.daysLeft >= 1 && sub.daysLeft <= 30)
      .sort((a, b) => a.daysLeft - b.daysLeft);
    setExpiringSoon(filtered);
  }, [subscriptions]);

  return { dataMonths, expiringSoon };
};

// ─── Extracted: build quickActions list ────────────────────────────────────────

const useQuickActions = (user: any, setSlideOverPanel: (p: string) => void) => {
  const baseActions = [
    {
      label: "Add Member",
      icon: (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#16a34a", borderRadius: "5%", width: 40, height: 40 }}>
          <Plus color="#ffffff" size={24} />
        </Box>
      ),
      onClick: () => setSlideOverPanel("add-member"),
    },
    {
      label: "Add Asset",
      icon: (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f59e0b", borderRadius: "5%", width: 40, height: 40 }}>
          <Package color="#fff" size={24} />
        </Box>
      ),
      onClick: () => setSlideOverPanel("add-asset"),
    },
    {
      label: "Add Asset Type",
      icon: (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#ef4444", borderRadius: "5%", width: 40, height: 40 }}>
          <Layers color="#fff" size={24} />
        </Box>
      ),
      onClick: () => setSlideOverPanel("add-asset-type"),
    },
  ];

  const adminActions = [
    {
      label: "Add Customer",
      icon: (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#8b5cf6", borderRadius: "5%", width: 40, height: 40 }}>
          <UserPlus color="#fff" size={24} />
        </Box>
      ),
      onClick: () => setSlideOverPanel("add-customer"),
    },
    {
      label: "Add Page",
      icon: (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#0d9488", borderRadius: "5%", width: 40, height: 40 }}>
          <Layers color="#fff" size={24} />
        </Box>
      ),
      onClick: () => setSlideOverPanel("add-page"),
    },
  ];

  const subscriptionAction = {
    label: "Add Subscription",
    icon: (
      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: "#3b82f6", borderRadius: "5%", width: 40, height: 40 }}>
        <FilePlus color="#fff" size={24} />
      </Box>
    ),
    onClick: () => setSlideOverPanel("add-subscription"),
  };

  const isAdminOrSupervisor = user?.Role.includes("admin") || user?.Role.includes("supervisor");
  const isAdmin = user?.Role.includes("admin");

  const conditionalActions = isAdminOrSupervisor
    ? (isAdmin ? adminActions : [])
    : [];

  return [...baseActions, ...conditionalActions, subscriptionAction];
};

// ─── Main Dashboard Component ──────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const clientCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";

  const [refreshInterval, setRefreshInterval] = useState(0);

  const {
    counts,
    totalCount,
    rating,
    todayTickets,
    latestTickets,
    activeCustomerCount,
    inActiveCustomerCount,
    activeEmployeeCount,
    inActiveEmployeeCount,
    activeAssetTypeCount,
    inActiveAssetTypeCount,
    assetCount,
    inActiveAssetCount,
    revenue,
  } = useDashboardData(user, refreshInterval);

  const { dataMonths, expiringSoon } = useSubscriptionData();

  type QuickActionPanel =
    | "add-member"
    | "add-customer"
    | "add-subscription"
    | "add-page"
    | "add-ticket"
    | "add-asset"
    | "add-asset-type"
    | null;

  const [slideOverPanel, setSlideOverPanel] = useState<QuickActionPanel>(null);
  const [addAssetCustomers, setAddAssetCustomers] = useState<any[] | null>(null);
  const closeSlideOver = () => setSlideOverPanel(null);

  const quickActions = useQuickActions(user, (p) => setSlideOverPanel(p as QuickActionPanel));

  const [selectedDateTickets, setSelectedDateTickets] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const storedInterval = localStorage.getItem("refreshInterval");
    if (storedInterval) {
      setRefreshInterval(Number(storedInterval));
    }
  }, []);

  useEffect(() => {
    if (slideOverPanel === "add-asset") {
      axios
        .get("/customer", { params: { is_active: 1 } })
        .then((res) => {
          const activeCustomers = (res.data?.list ?? []).filter(
            (entry: { is_active?: number | boolean }) =>
              entry?.is_active === 1 || entry?.is_active === true
          );
          setAddAssetCustomers(activeCustomers);
        })
        .catch(() => setAddAssetCustomers([]));
    }
  }, [slideOverPanel]);

  const handleRefreshIntervalChange = (newInterval: string | number) => {
    const interval = Number(newInterval);
    setRefreshInterval(interval);
    localStorage.setItem("refreshInterval", String(interval));
  };

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      const filteredTickets = todayTickets.filter((ticket: Ticket) =>
        ticketOnCalendarDay(ticket, date)
      );
      setSelectedDateTickets(filteredTickets);
    },
    [todayTickets]
  );

  useEffect(() => {
    if (todayTickets.length > 0) {
      handleDateSelect(new Date());
    }
  }, [todayTickets, handleDateSelect]);

  const sortedTodayTicketsForEmployee = useMemo((): Ticket[] => {
    if (!user?.Role.includes("employee")) return [];
    const list = (todayTickets as Ticket[]).filter(
      (t: Ticket) =>
        parseArrivalDateOnlyLocal(t.employee_arrival_date)?.toDateString() ===
        new Date().toDateString()
    );
    return [...list].sort((a, b) => {
      const aMissing = !a.assigned_on ? 1 : 0;
      const bMissing = !b.assigned_on ? 1 : 0;
      if (aMissing !== bMissing) return aMissing - bMissing;
      const at = a.assigned_on ? new Date(a.assigned_on).getTime() : 0;
      const bt = b.assigned_on ? new Date(b.assigned_on).getTime() : 0;
      if (bt !== at) return bt - at;
      return b.ticket_id - a.ticket_id;
    });
  }, [todayTickets, user?.Role]);

  const statusColorValues = Object.values(statusColors);
  const statusMap: Record<string, number> = {
    Open: 1,
    ToDo: 2,
    "In-Progress": 3,
    "On-Hold": 4,
    Pending: 5,
    Done: 6,
    Closed: 7,
    "Re-Open": 8,
    Cancelled: 9,
  };

  const pieData = (() => {
    const entries = Object.entries(counts);
    let runningTotal = 0;
    return entries.map(([status, { total_count }], index) => {
      const isLast = index === entries.length - 1;
      const raw = ((total_count ?? 0) / totalCount) * 100;
      const percentage = isLast
        ? (100 - runningTotal).toFixed(2)
        : raw.toFixed(2);
      if (!isLast) {
        runningTotal += parseFloat(raw.toFixed(2));
      }
      return {
        name: status,
        status_id: statusMap[status],
        value: total_count ?? 0,
        percentage,
        color: statusColorValues[index % statusColorValues.length],
      };
    });
  })();

  const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle = 0,
    outerRadius,
    percent = 0,
  }: SafePieLabelProps) => {
    const cxNum = typeof cx === "number" ? cx : 0;
    const cyNum = typeof cy === "number" ? cy : 0;
    const outerNum = typeof outerRadius === "number" ? outerRadius : 0;
    const radius = outerNum + 30;
    const x = cxNum + radius * Math.cos(-midAngle * RADIAN);
    const y = cyNum + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cxNum ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const cardStyle = {
    height: "100%",
    boxShadow: 3,
    borderRadius: 2,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: 5,
      transform: "scale(1.05)",
    },
  };

  const safeRating = Number.isFinite(rating) ? Math.max(Number(rating), 0) : 0;
  const fullStars = Math.floor(safeRating);
  const hasHalfStar = safeRating % 1 >= 0.5;
  const emptyStars = 5 - Math.ceil(safeRating);

  const handleClick = (status_id: string | number) => {
    if (user?.Role.includes("employee")) {
      navigate(`/employee-tickets-list?status_id=${status_id}`);
    } else {
      navigate(`/admin/tickets-list?status_id=${status_id}`);
    }
  };

  // --- Extracted: check subscription expiry banner visibility ---
  const gracePeriodRemaining = Number(user?.gracePeriodRemaining) || 0;
  const showGracePeriodBanner =
    user?.isSubscriptionExpired &&
    gracePeriodRemaining > 0 &&
    isUserAdmin(user?.Role);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/*------------------------------------------------////Admin////---------------------------------------------------------------------*/}
      {showGracePeriodBanner && (
        <Box
          sx={{
            bgcolor: "#fff4e5",
            color: "#7c4d00",
            p: 2,
            borderRadius: 1,
            mb: 2,
            border: "1px solid #ffe0b2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Your subscription has expired. You have{" "}
            <strong>{user?.gracePeriodRemaining}</strong>{" "}
            {user?.gracePeriodRemaining === 1 ? "day" : "days"} remaining in
            your grace period. Please update your payment to avoid service
            interruption.
          </Typography>
        </Box>
      )}
      {user?.freeTierComplete && user?.Role?.includes("admin") && (
        <Box
          sx={{
            bgcolor: "#fff4e5",
            color: "#7c4d00",
            p: 2,
            borderRadius: 1,
            mb: 2,
            border: "1px solid #ffe0b2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Your free tier is complete. Subscribe now to add more customers and
            use all features.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/subscribe")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Subscribe now
          </Button>
        </Box>
      )}
      {(user?.Role.includes("admin") ||
        user?.Role.includes("supervisor") ||
        user?.Role.includes("superAdmin")) && (
        <Box sx={{ flexGrow: 1, mt: 3 }}>
          {/*----------------Title and Refresh time-------------- */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid
              item
              xs={12}
              md={10}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography variant="h4" gutterBottom sx={{ fontSize: "18px" }}>
                {user.Role.includes("admin") ??
                user.Role.includes("superAdmin") ? (
                  <strong>Admin Dashboard</strong>
                ) : (
                  <strong>Supervisor Dashboard</strong>
                )}
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontSize: "14px" }}
              >
                Overview of system performance and ticket management
              </Typography>
            </Grid>
            {user.Role.includes("admin") && (
              <Grid
                item
                xs={12}
                md={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <FormControl sx={{ mr: 2, minWidth: 150, fontSize: "12px" }}>
                  <Typography sx={{ fontSize: 12 }}>Auto Refresh</Typography>
                  <Autocomplete
                    options={[
                      { label: "Off", value: 0 },
                      { label: "2 minutes", value: 120000 },
                      { label: "5 minutes", value: 300000 },
                      { label: "10 minutes", value: 600000 },
                    ]}
                    getOptionLabel={(option) => option.label}
                    value={
                      [
                        { label: "Off", value: 0 },
                        { label: "2 minutes", value: 120000 },
                        { label: "5 minutes", value: 300000 },
                        { label: "10 minutes", value: 600000 },
                      ].find((option) => option.value === refreshInterval) ??
                      null
                    }
                    onChange={(event, newValue) =>
                      handleRefreshIntervalChange(newValue?.value ?? 0)
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
          {/*-------------- Summary Cards ----------------*/}
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.4}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  padding: 0,
                  "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                }}
                onClick={() => navigate("/customers-list")}
              >
                <CardContent
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px" }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, fontSize: "14px" }}>
                      Customers
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mt: 0.5 }}>
                      {activeCustomerCount + inActiveCustomerCount}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.6, mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "green", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/customers-list?status=active"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {activeCustomerCount} Active
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "gray", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/customers-list?status=inactive"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {inActiveCustomerCount} Inactive
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: 40, height: 40, borderRadius: "10%", background: "linear-gradient(200deg, #FFFFFF, #00BFFF)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <GroupsIcon sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  padding: 0,
                  "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                }}
                onClick={() => navigate("/employees-list")}
              >
                <CardContent
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px" }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, fontSize: "14px" }}>
                      Members
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mt: 0.5 }}>
                      {activeEmployeeCount + inActiveEmployeeCount}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.4, mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "green", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/employees-list?status=active"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {activeEmployeeCount} Active
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "gray", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/employees-list?status=inactive"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {inActiveEmployeeCount} Inactive
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: 40, height: 40, borderRadius: "10%", background: "linear-gradient(135deg, #32CD32, #FFFFFF)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <GroupsIcon sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {user.Role.includes("admin") && (
              <Grid item xs={12} md={2.4}>
                <Card
                  sx={{
                    ...cardStyle,
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                  }}
                  onClick={() => navigate("/subscriptions-list")}
                >
                  <CardContent
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px" }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px", color: "#6b7280" }}>
                        Subscriptions Amount
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold", wordBreak: "break-word" }}>
                        <strong>
                          {formatMoney(Number(revenue ?? 0), String(clientCurrency || "USD"))}
                        </strong>
                      </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: "10%", background: "linear-gradient(135deg, #FFFFFF, #800080)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <Typography sx={{ color: "white", fontSize: 18, fontWeight: 800, lineHeight: 1 }}>
                        {formatMoney(0, String(clientCurrency || "USD")).replace(/[0-9.,\s]/g, "").trim() || String(clientCurrency || "USD")}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={2.4}>
              <Card
                sx={{ "&:hover": { boxShadow: 6, transform: "scale(1.05)" }, borderRadius: 2, boxShadow: 2, cursor: "pointer" }}
                onClick={() => navigate("/types")}
              >
                <CardContent
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px" }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, fontSize: "14px" }}>
                      Asset Types
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mt: 0.5 }}>
                      {activeAssetTypeCount + inActiveAssetTypeCount}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.4, mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "green", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/types?status=active"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {activeAssetTypeCount} Active
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "gray", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/types?status=inactive"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {inActiveAssetTypeCount} Inactive
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: 40, height: 40, borderRadius: "10%", background: "linear-gradient(135deg, #32CD32, #FFFFFF)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <WorkspacesIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card
                sx={{
                  ...cardStyle,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  padding: 0,
                  "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                }}
                onClick={() => navigate("/assets-list")}
              >
                <CardContent
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px" }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, fontSize: "14px" }}>
                      Assets
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", mt: 0.5 }}>
                      {assetCount + inActiveAssetCount}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.4, mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "green", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/assets-list?status=active"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {assetCount} Active
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "gray", fontWeight: 600, fontSize: "11px" }}
                        onClick={(e) => { e.stopPropagation(); navigate("/assets-list?status=inactive"); }}
                      >
                        <Box component="span" sx={{ display: "inline-block", transform: "scale(1.5)", transformOrigin: "center", marginRight: "2px" }}>&bull;</Box>
                        {inActiveAssetCount} Inactive
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: 40, height: 40, borderRadius: "10%", background: "linear-gradient(200deg, #FF7F7F, #FFFFFF)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <GrainIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {/*--------Graphs ------*/}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  height: "100%",
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { boxShadow: 3, transform: "scale(1.05)" },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    Subscription Trends
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#2e9f6e" }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Active</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#10b981" }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>New</Typography>
                      </Box>
                    </Box>
                  </Typography>
                  <div style={{ width: "100%", height: "300px" }}>
                    <ResponsiveContainer>
                      <LineChart data={dataMonths}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" interval={0} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="active" stroke="#2e9f6e" strokeWidth={3} dot={false} />
                        <Line type="monotone" dataKey="new" stroke="#10b981" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { boxShadow: 3, transform: "scale(1.05)" },
                }}
              >
                <CardContent sx={{ height: 380, display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    Expiring Soon
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box
                        component={Link}
                        to="/expiring-subscriptions-list"
                        onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
                        sx={{ textDecoration: "none" }}
                      >
                        <SoftTypography
                          sx={{ cursor: "pointer", fontWeight: "bold", color: "#6c87f5", fontSize: 14 }}
                        >
                          View All
                        </SoftTypography>
                      </Box>
                    </Box>
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      pr: 0.5,
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: 8 },
                    }}
                  >
                    {expiringSoon.length > 0 ? (
                      expiringSoon.map((sub) => (
                        <Card
                          key={sub.id}
                          sx={{ mb: 2, borderRadius: 2, backgroundColor: "#fdecec", cursor: "pointer", transition: "0.3s ease", "&:hover": { boxShadow: 4, transform: "scale(1.02)" } }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                  <Typography
                                    fontWeight="bold"
                                    fontSize={14}
                                    sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                                  >
                                    {sub.name}
                                  </Typography>
                                  <Chip
                                    label={sub.asset_name}
                                    size="small"
                                    sx={{ fontSize: 11, height: 20, backgroundColor: "#fff", fontWeight: "bold", maxWidth: "100%" }}
                                  />
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                  <Typography fontSize={12} color="text.secondary">📅 Expires in</Typography>
                                  <Typography fontSize={12} color="error" fontWeight="bold">{sub.daysLeft} days</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: "fit-content", maxWidth: "45%" }}>
                                <Typography
                                  fontWeight="bold"
                                  fontSize={16}
                                  sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                  title={`${formatMoney(sub.price, "INR")}/mo`}
                                >
                                  {formatMoney(sub.price, "INR")}/mo
                                </Typography>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: getStatusIndicatorColor(sub.daysLeft),
                                    mt: 1,
                                    ml: "auto",
                                  }}
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 14 }}>
                        No Upcoming Expiring Soon Subscriptions
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {/*--------------- Monthly ticket overview , Ticket status Distribution and status overview*----------*/}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", textAlign: "left" }}>
                    Ticket Status Distribution
                  </Typography>
                  {totalCount > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
                      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                        <ResponsiveContainer width="80%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              outerRadius={55}
                              dataKey="value"
                              labelLine
                              label={(props) => renderCustomizedLabel(props as SafePieLabelProps)}
                            >
                              {pieData.map((entry) => (
                                <Cell key={entry.name} fill={statusColors[entry.name] ?? "#757575"} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value, name, { payload }) => [
                                `${value} (${payload.percentage}%)`,
                                name,
                              ]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                        {pieData.map((entry) => (
                          <Box
                            key={entry.name}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 0.5,
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                              backgroundColor: statusColors[entry.name] ?? "#757575",
                            }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/tickets-list?status_id=${entry.status_id}`); }}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: "bold", mb: 0.5, textAlign: "center", color: "#fff" }}>
                              {entry.name}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#fff", textAlign: "center" }}>
                              {entry.percentage}%
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" align="center" sx={{ mt: 5 }}>
                      No ticket data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { boxShadow: 3, transform: "scale(1.05)" },
                }}
              >
                <CardContent>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 1, borderBottom: "1px solid #e0e0e0" }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>Recent Tickets</Typography>
                    <SoftTypography
                      sx={{ cursor: "pointer", fontWeight: "bold", color: "#6c87f5", fontSize: 14 }}
                      onClick={() =>
                        navigate(
                          user.Role.includes("employee")
                            ? "/employee-tickets-list"
                            : "/admin/tickets-list"
                        )
                      }
                    >
                      View All
                    </SoftTypography>
                  </Box>
                  <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                    {latestTickets.length > 0 ? (
                      latestTickets.map((ticket: Ticket) => {
                        const getStatusColor = (status: string) =>
                          statusColors[status] ?? "#757575";

                        let assignmentText: string;
                        if (ticket.customer_name) {
                          assignmentText = `Customer: ${ticket.customer_name}`;
                        } else if (ticket.employee_name) {
                          assignmentText = `Assigned to: ${ticket.employee_name}`;
                        } else {
                          assignmentText = "Unassigned";
                        }

                        return (
                          <Card
                            key={ticket.ticket_id}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              cursor: "pointer",
                              bgcolor: "#f9fafb",
                              backgroundSize: "200% auto",
                              transition: "0.5s all ease",
                              "&:hover": { backgroundPosition: "right center", boxShadow: 3, borderColor: "#6c87f5", transform: "scale(1.02)" },
                            }}
                            onClick={() => navigate(`/admin/ticket-view/${ticket.ticket_id}`)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "14px" }}>
                                  {ticket.ticket_service_id}
                                </Typography>
                                <Chip
                                  label={ticket.status_name}
                                  size="small"
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    color: "#fff",
                                    "& .MuiChip-label": { color: "#fff", fontWeight: 700 },
                                    backgroundColor: getStatusColor(ticket.status_name),
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" fontWeight={"bold"} sx={{ mb: 1, color: "text.secondary", fontSize: "14px" }}>
                                <strong>{ticket.title ?? ticket.description}</strong>
                              </Typography>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="caption" fontWeight={"bold"} sx={{ color: "text.secondary" }}>
                                  {assignmentText}
                                </Typography>
                                <Typography variant="caption" fontWeight={"bold"} sx={{ color: "text.disabled" }}>
                                  {dayjs(ticket.created_at).format("MMM D, YYYY")}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No tickets found
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": { boxShadow: 6, transform: "scale(1.05)" },
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2}>
                    {quickActions.map((action) => (
                      <Grid item xs={6} key={action.label}>
                        <Box
                          sx={{
                            height: 110,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "#f9fafb",
                            minWidth: 130,
                            minHeight: 100,
                            boxShadow: 2,
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": { boxShadow: 4, transform: "scale(1.05)" },
                          }}
                          onClick={action.onClick}
                        >
                          <IconButton sx={{ mb: 1 }}>{action.icon}</IconButton>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, textAlign: "center", whiteSpace: "normal", wordBreak: "break-word", maxWidth: "100%", fontSize: "12px" }}
                          >
                            {action.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      {/*-----------------------------------------------////Employee////------------------------------------------------------------------*/}
      <Grid container spacing={3} className="static-cards">
        {user?.Role.includes("employee") && (
          <Box sx={{ flexGrow: 1, mt: 3 }}>
            <Grid
              container
              spacing={3}
              sx={{ mb: 3, alignItems: "center", top: 0, zIndex: 10, py: 1 }}
            >
              <Grid item xs={12} md>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                  <strong>Employee Dashboard</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your daily tasks and track your assigned tasks
                </Typography>
              </Grid>
              <Grid item md="auto" sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <Card
                  sx={{
                    minWidth: 120,
                    px: 2,
                    py: 1,
                    boxShadow: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { boxShadow: 4, transform: "scale(1.05)" },
                  }}
                >
                  <Typography variant="h6" sx={{ m: 0, lineHeight: 1.2 }}>
                    <strong>{safeRating}</strong>{" "}
                    <Box component="span" sx={{ color: "#FFD700", fontSize: "1.1em" }}>
                      {"\u2605".repeat(fullStars)}
                      {hasHalfStar ? "\u00BD" : ""}
                      {"\u2606".repeat(emptyStars)}
                    </Box>
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            {/*-----------Ticket status Distribution and status overview--------*/}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Grid>
                  <Card
                    sx={{
                      height: "100%",
                      boxShadow: 3,
                      borderRadius: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": { boxShadow: 3, transform: "scale(1.05)" },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}>
                        Ticket Status Distribution
                      </Typography>
                      {totalCount > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", height: 400 }}>
                          <Box sx={{ flex: 1 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieData}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  dataKey="value"
                                  labelLine={true}
                                  label={renderCustomizedLabel}
                                >
                                  {pieData.map((entry) => (
                                    <Cell key={`cell-${entry.name}`} fill={statusColors[entry.name] ?? "#757575"} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  formatter={(value, name, { payload }) => [
                                    `${value} (${payload.percentage}%)`,
                                    name,
                                  ]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mt: 2, maxHeight: 100, overflowY: "auto" }}>
                            {pieData.map((entry) => (
                              <Box key={entry.name} sx={{ display: "flex", alignItems: "center", mr: 2, mb: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: statusColors[entry.name] ?? "#757575", mr: 1 }} />
                                <Typography variant="body2" sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                                  {entry.name}: {entry.percentage}%
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" align="center" sx={{ mt: 5 }}>
                          No ticket data available
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { boxShadow: 3, transform: "scale(1.05)" },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>Status Overview</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                      <Chip
                        label={`Total tickets ${totalCount}`}
                        sx={{
                          minWidth: 120,
                          bgcolor: "teal",
                          color: "#fff",
                          fontWeight: 700,
                          transition: "all 0.2s ease-in-out",
                          "& .MuiChip-label": { color: "#fff", fontWeight: 700 },
                          "&:hover": { boxShadow: 3, transform: "scale(1.05)", bgcolor: "#000000" },
                        }}
                        onClick={() => navigate("/employee-tickets-list")}
                      />
                      {Object.entries(counts)
                        .filter(([status]) => status !== "Open")
                        .map(([status, { status_id, total_count }]) => (
                          <Chip
                            key={status_id}
                            label={`${status} ${total_count}`}
                            sx={{
                              minWidth: 120,
                              bgcolor: statusColors[status] ?? "#757575",
                              color: "#fff",
                              fontWeight: 700,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": { boxShadow: 3, transform: "scale(1.05)", bgcolor: "#000000" },
                            }}
                            onClick={() => handleClick(status_id)}
                          />
                        ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {/* -----------------Today's Tickets and Calendar Section---------------------*/}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { boxShadow: 3, transform: "scale(1.02)" },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 1, borderBottom: "1px solid #e0e0e0" }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        <AccessTimeIcon sx={{ pt: 0.3, color: "#769ee3" }} /> Today's Tickets
                      </Typography>
                      <Typography
                        fontSize={14}
                        color="#6c87f5"
                        sx={{ cursor: "pointer", fontWeight: "bold" }}
                        onClick={() =>
                          navigate(
                            user.Role.includes("employee")
                              ? "/employee-tickets-list"
                              : "/admin/tickets-list"
                          )
                        }
                      >
                        View All
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        maxHeight: 420,
                        overflowY: "auto",
                        overflowX: "hidden",
                        pr: 0.5,
                        "&::-webkit-scrollbar": { width: "8px" },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: "8px",
                        },
                      }}
                    >
                    {sortedTodayTicketsForEmployee.map((ticket: Ticket) => {
                        let priorityBg: string;
                        let priorityText: string;

                        if (ticket.priority_rank === "High") {
                          priorityBg = "#fce3e1";
                          priorityText = "#e53935";
                        } else if (ticket.priority_rank === "Medium") {
                          priorityBg = "#e0e0da";
                          priorityText = "#ab9703";
                        } else {
                          priorityBg = "#edebdd";
                          priorityText = "#424242";
                        }

                        return (
                          <Card
                            key={ticket.ticket_id}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              background: "#fff",
                              transition: "0.3s all ease",
                              "&:hover": { boxShadow: 3, borderColor: "#6c87f5", cursor: "pointer" },
                            }}
                            onClick={() =>
                              navigate(`/employee/ticket-view/${ticket.ticket_id}`, {
                                state: { from: "/dashboard" },
                              })
                            }
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "black",fontSize: "0.9rem" }}>
                                      {ticket.ticket_service_id}
                                    </Typography>
                                    <Chip
                                      label={ticket.status_name}
                                      size="small"
                                      sx={{
                                        borderRadius: 2,
                                        fontSize: "0.7rem",
                                        height: 22,
                                        fontWeight: "bold",
                                        bgcolor: statusColors[ticket.status_name] ?? "#757575",
                                        "& .MuiChip-label": { color: "#fff !important", fontWeight: "bold" },
                                      }}
                                    />
                                    <Chip
                                      label={ticket.priority_rank}
                                      size="small"
                                      sx={{ borderRadius: 2, fontSize: "0.7rem", height: 22, fontWeight: "bold", backgroundColor: priorityBg, color: priorityText }}
                                    />
                                  </Box>
                                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 ,fontSize: "0.9rem"}}>
                                    {ticket.title}
                                  </Typography>
                                  <Typography variant="h6" color="text.secondary" sx={{fontSize: "0.9rem"}}>
                                    {ticket.company_name}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right", minWidth: 120 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: "bold" }}>
                                    <AccessTimeIcon /> Arrival On
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: "block", fontWeight: 500 }}>
                                    {parseArrivalDateOnlyLocal(
                                      ticket.employee_arrival_date
                                    )?.toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    }) ?? "-"}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                    {sortedTodayTicketsForEmployee.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No tickets scheduled for today.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Card
                  sx={{
                    height: "100%",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { boxShadow: 3, transform: "scale(1.02)" },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                      <CalendarMonthIcon sx={{ pt: 0.3, color: "#769ee3" }} /> Tickets Calendar
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TicketCalendar tickets={todayTickets} onDateSelect={handleDateSelect} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {selectedDate && (
                          <Box sx={{ height: 400, overflowY: "auto", pr: 1 }}>
                            <Box sx={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1, pb: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                <strong>
                                  {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                                </strong>
                              </Typography>
                            </Box>
                            {selectedDateTickets.length > 0 ? (
                              selectedDateTickets.map((ticket: Ticket) => {
                                let cardBgColor: string;
                                let chipBgColor: string;
                                let chipTextColor: string;

                                if (ticket.priority_rank === "High") {
                                  cardBgColor = "#fdecea";
                                  chipBgColor = "#fce3e1";
                                  chipTextColor = "#e53935";
                                } else if (ticket.priority_rank === "Medium") {
                                  cardBgColor = "#fff7e6";
                                  chipBgColor = "#e0e0da";
                                  chipTextColor = "#ab9703";
                                } else {
                                  cardBgColor = "#f4f4f4";
                                  chipBgColor = "#edebdd";
                                  chipTextColor = "#424242";
                                }

                                return (
                                  <Card
                                    key={ticket.ticket_id}
                                    variant="outlined"
                                    sx={{
                                      mb: 2,
                                      borderRadius: 2,
                                      backgroundColor: cardBgColor,
                                      transition: "0.3s all ease",
                                      "&:hover": { boxShadow: 3, borderColor: "#6c87f5", cursor: "pointer" },
                                    }}
                                    onClick={() =>
                              navigate(`/employee/ticket-view/${ticket.ticket_id}`, {
                                state: { from: "/dashboard" },
                              })
                            }
                                  >
                                    <CardContent sx={{ p: 2 }}>
                                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Typography variant="h6" sx={{ fontWeight: "bold", color: "black",fontSize:"0.9rem" }}>
                                            {ticket.ticket_service_id}
                                          </Typography>
                                          <Chip
                                            label={ticket.priority_rank}
                                            size="small"
                                            sx={{ borderRadius: 2, fontSize: "0.7rem", height: 22, fontWeight: "bold", backgroundColor: chipBgColor, color: chipTextColor }}
                                          />
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#666" }}>
                                          <AccessTimeIcon sx={{ pt: 0.3}} />{" "}
<Typography
  sx={{
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#333",
  }}
>
  {(() => {
    const ar = parseTicketArrivalDateTime(
      ticket.employee_arrival_date,
      ticket.employee_arrival_time
    );

    if (ar)
      return ar.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

    return new Date(ticket.created_at).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  })()}
</Typography>
                                        </Typography>
                                      </Box>
                                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5,fontSize:"0.9rem" }}>
                                        {ticket.title}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                );
                              })
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                                No tickets for this date.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Grid>
      {/*-----------------------------------------------////Stock Manager////------------------------------------------------------------------*/}
      {user?.Role.includes("stockManager") && <StockManagerDashboard />}
      {/*-----------------------------------------------////Branch Head////------------------------------------------------------------------*/}
      {user?.Role.includes("BranchHead") && <BranchHeadDashboard />}

      <SlideOverPanel
        open={slideOverPanel !== null}
        onClose={closeSlideOver}
        title={getSlideOverTitle(slideOverPanel)}
        width={1000}
      >
        {slideOverPanel === "add-member" && (
          <AddEmployee embedded onSuccess={closeSlideOver} onCancel={closeSlideOver} />
        )}
        {slideOverPanel === "add-customer" && (
          <AddCustomers embedded onSuccess={closeSlideOver} onCancel={closeSlideOver} />
        )}
        {slideOverPanel === "add-subscription" && (
          <AddSubscription embedded onSuccess={closeSlideOver} onCancel={closeSlideOver} />
        )}
        {slideOverPanel === "add-page" && (
          <AddPage embedded onSuccess={closeSlideOver} onCancel={closeSlideOver} />
        )}
        {slideOverPanel === "add-ticket" && (
          <AdminAddTicket embedded onSuccess={closeSlideOver} onCancel={closeSlideOver} />
        )}
        {slideOverPanel === "add-asset" && (
          <AddAsset isOpen={true} onClose={closeSlideOver} customers={addAssetCustomers} fetchData={closeSlideOver} embedded />
        )}
        {slideOverPanel === "add-asset-type" && (
          <AddAssetType isOpen={true} onClose={closeSlideOver} fetchData={closeSlideOver} embedded />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default Dashboard;
